/**
 * Query-key factory for React Query.
 *
 * Construct one instance per resource with its basePath so the first key
 * segment is fixed for that instance. Context still sits in the key — two
 * calls with the same id and different context must not share a cache entry.
 * Nested URLs belong in the constructor (`/projects/${projectId}/positions`),
 * not in a getBasePath lookup.
 *
 * List keys are [basePath, context, "list", { page, pageSize, sortBy, sortDir, ...filters }].
 * PagedResult has no totalPages (only content, pageNumber, totalElements); use QueryKeyFactory.totalPages().
 */

export type ServiceContext = Record<string, string | number>;
export type QueryParamValue = string | number | boolean | undefined;

/** useWorkLogList page keys. The list caches for 60s, so approval must invalidate this context. */
export const WORKLOG_DAILY_LIST_CONTEXT: ServiceContext = { scope: "daily-list" };

export class QueryKeyFactory {
  constructor(private readonly basePath: string) {}

  private listParams<T extends Record<string, QueryParamValue>>(params: T): T {
    const result = { ...params };

    for (const key of Object.keys(result)) {
      if (result[key] === undefined) {
        delete result[key];
      }
    }

    return result;
  }

  public all(context: ServiceContext = {}) {
    return [this.basePath, { ...context }] as const;
  }

  public lists(context: ServiceContext = {}) {
    return [this.basePath, { ...context }, "list"] as const;
  }

  public list<T extends Record<string, QueryParamValue>>(params: T, context: ServiceContext = {}) {
    return [this.basePath, { ...context }, "list", this.listParams(params)] as const;
  }

  public details(context: ServiceContext = {}) {
    return [this.basePath, { ...context }, "detail"] as const;
  }

  public detail(id: string | number, context: ServiceContext = {}) {
    return [this.basePath, { ...context }, "detail", id] as const;
  }

  public static totalPages(result: { totalElements: number }, pageSize: number): number {
    if (pageSize <= 0) {
      return 0;
    }
    return Math.ceil(result.totalElements / pageSize);
  }
}
